import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { MapPin, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * Address autocomplete that surfaces validated Photon (OSM) suggestions.
 * On pick → calls `onPick({ label, lat, lng })` AND fires the regular `onChange(label)`.
 * If the user types freely without picking, the parent should geocode on save (backend already does this).
 */

// Session-wide suggestion cache, shared by every address box on the page.
// Dispatchers backspace and retype the same streets constantly; the upstream
// lookup is the slowest call in the app, so a repeat is instant.
const SUGGESTION_CACHE = new Map();
const CACHE_MAX = 200;

function remember(q, list) {
  const key = q.toLowerCase();
  if (SUGGESTION_CACHE.size >= CACHE_MAX) {
    SUGGESTION_CACHE.delete(SUGGESTION_CACHE.keys().next().value);
  }
  SUGGESTION_CACHE.set(key, list);
}
export default function AddressAutocomplete({
  value,
  onChange,
  onPick,
  placeholder,
  testIdPrefix = "addr",
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [pickedLabel, setPickedLabel] = useState(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch. Each keystroke aborts the previous in-flight request —
  // the upstream geocoder can take seconds, and without this a dispatcher
  // typing a full address leaves half a dozen requests queued (browsers cap
  // 6 per host) and an older response can land after a newer one and
  // repaint the list with stale suggestions (Jun 2026).
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 4 || value === pickedLabel) {
      abortRef.current?.abort();
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const cached = SUGGESTION_CACHE.get(q.toLowerCase());
    if (cached) {
      abortRef.current?.abort();
      setSuggestions(cached);
      setActiveIdx(-1);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const r = await api.get("/geocode/autocomplete", {
          params: { q, limit: 6 },
          signal: ctrl.signal,
        });
        const next = r.data?.suggestions || [];
        if (next.length) remember(q, next);
        setSuggestions(next);
        setActiveIdx(-1);
      } catch (e) {
        if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
        setSuggestions([]);
      } finally {
        if (abortRef.current === ctrl) setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value, pickedLabel]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (s) => {
    setPickedLabel(s.label);
    onChange(s.label);
    onPick?.({ label: s.label, lat: s.lat, lng: s.lng });
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && (loading || suggestions.length > 0);

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value || ""}
        onChange={(e) => {
          if (pickedLabel && e.target.value !== pickedLabel) setPickedLabel(null);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        data-testid={`${testIdPrefix}-input`}
      />
      {pickedLabel === value && value && (
        <Check
          className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600 pointer-events-none"
          data-testid={`${testIdPrefix}-picked-check`}
        />
      )}
      {loading && !pickedLabel && (
        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin pointer-events-none" />
      )}
      {showDropdown && (
        <div
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching addresses…
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.osm_type}-${s.osm_id}-${i}`}
              onClick={() => pick(s)}
              onMouseEnter={() => setActiveIdx(i)}
              className={cn(
                "w-full flex items-start gap-2 text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0",
                activeIdx === i ? "bg-blue-50" : "hover:bg-slate-50"
              )}
              data-testid={`${testIdPrefix}-option-${i}`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-blue-600 shrink-0" />
              <span className="text-slate-700 leading-tight flex-1">{s.label}</span>
              {s.kind && (
                <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">
                  {s.kind}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
